import { ApolloClient, HttpLink, ApolloLink, Observable } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { CONSTANTS } from './service/constants';
import { getToken, trySilentRefresh, persistTokenFromResponse } from './service/request';
import store from './redux/store';
import cache from './graphql/GraphqlPolicies/MemoryCache.ts';

// GraphQL lives on the API host (e.g., https://stage-api.nettgrav.no/graphql)
const graphQLink = new HttpLink({
  uri: `${CONSTANTS.baseUrl}/graphql`,
  credentials: 'include',
});

const authLink = setContext((_, { headers }) => {
  const token = getToken() || '';
  let userId = undefined;
  try {
    const state = store.getState();
    userId = state?.user?.user?.id;
  } catch {}

  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : '',
      ...(userId ? { 'X-User-Id': userId } : {}),
    },
  };
});

// On 401: perform one silent refresh, then retry the operation
const refreshLink = onError(({ networkError, operation, forward }) => {
  const status = networkError?.statusCode || networkError?.response?.status;
  const alreadyRetried = operation.getContext()._retried;

  if (status === 401 && !alreadyRetried) {
    return new Observable((observer) => {
      let innerSub;
      let completed = false;

      trySilentRefresh()
        .then((ok) => {
          if (!ok) {
            throw networkError;
          }

          if (completed) {
            return;
          }

          const prevContext = operation.getContext() || {};
          const prevHeaders = prevContext.headers || {};
          const refreshedToken = getToken();
          const nextHeaders = { ...prevHeaders };

          if (refreshedToken) {
            nextHeaders.Authorization = `Bearer ${refreshedToken}`;
          } else {
            delete nextHeaders.Authorization;
          }

          operation.setContext({
            ...prevContext,
            _retried: true,
            headers: nextHeaders,
          });
          const forwardObservable = forward?.(operation);

          if (!forwardObservable) {
            observer.complete();
            return;
          }

          innerSub = forwardObservable.subscribe({
            next: (value) => observer.next(value),
            error: (error) => observer.error(error),
            complete: () => observer.complete(),
          });
        })
        .catch((error) => observer.error(error));

      return () => {
        completed = true;
        innerSub?.unsubscribe();
      };
    });
  }
  // fall through for non-401 errors
  return undefined;
});

const afterwareLink = new ApolloLink((operation, forward) => {
  const forwardObservable = forward?.(operation);
  if (!forwardObservable) {
    return null;
  }

  return new Observable((observer) => {
    const subscription = forwardObservable.subscribe({
      next: (response) => {
        const context = operation.getContext();
        const headers = context.response?.headers;
        if (headers) {
          persistTokenFromResponse(headers);
        }
        observer.next(response);
      },
      error: (error) => observer.error(error),
      complete: () => observer.complete(),
    });

    return () => subscription.unsubscribe();
  });
});

const client = new ApolloClient({
  link: ApolloLink.from([authLink, refreshLink, afterwareLink, graphQLink]),
  cache: cache,
});

export default client;
